import classnames from 'classnames';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { LocationWithCoordinates } from 'lib/types';
import { useDispatch } from 'react-redux';
import { selectDate, selectLocation } from 'booking/redux/actions';
import { Slot } from 'booking/lib/types';
import { Link } from 'gatsby';
import NearbyLocation, {
    SelectedSlot,
} from '../NearbyLocationsTimeSlots/NearbyLocation';
import * as styles from '../DateForm/DateForm.module.scss';
import { NearbySlotsContext } from '../NearbySlotsProvider';
import useTrackBookingStep from '../../hooks/useTrackBookingStep';
import DigitalWalkInBooking from '../DigitalWalkInBooking';

export interface LowAvailabilityDateFormProps {
    location: LocationWithCoordinates | null;
    ungroupedSlots: ReadonlyArray<Slot> | null;
    showHeader?: boolean;
    selectSlotAndGoNext: (time: string) => void;
    selectedSlot?: string;
    selectedDate: string;
    setSelectedNearbySlot: (selectedSlot: SelectedSlot) => void;
    isToday: boolean;
    isMobile: boolean;
    isClosed: boolean;
    classes?: Partial<typeof styles>;
    shouldDisplayWalkInComponent: boolean;
    setDigitalWalkInModalOpen: (value: boolean) => void;
    eventPrefix: string;
}

export default function NoAvailabilityDateForm({
    location,
    ungroupedSlots,
    showHeader,
    selectSlotAndGoNext,
    selectedSlot,
    selectedDate,
    setSelectedNearbySlot,
    isToday,
    isMobile,
    isClosed,
    classes,
    shouldDisplayWalkInComponent,
    setDigitalWalkInModalOpen,
    eventPrefix,
}: LowAvailabilityDateFormProps): React.ReactElement {
    const dispatch = useDispatch();
    const intl = useIntl();

    const trackBookingStep = useTrackBookingStep();
    const trackSameDay = React.useCallback(() => {
        trackBookingStep('same_day');
    }, [trackBookingStep]);

    const changeLocation = (id: string) => {
        dispatch(selectDate(selectedDate));
        dispatch(selectLocation(id));
    };

    const { hasSlots: hasNearbySlots } = React.useContext(NearbySlotsContext);

    const intlPostfix = hasNearbySlots ? 'With' : 'No';
    const actionIntlId = `booking.form.date.noAvailability${intlPostfix}Nearby`;
    const showHeaderPrefix = isClosed ? 'closed' : 'noAvailabilityTitle';
    const showHeaderIntlId = `booking.form.date.${showHeaderPrefix}`;
    const to = '/same-day';
    return (
        <div
            className={classnames(classes?.slots, styles.slots)}
            data-cy="slotsContent"
        >
            {ungroupedSlots?.map(({ time, label }: Slot) => (
                <button
                    type="button"
                    data-cy="time"
                    onClick={() => selectSlotAndGoNext(time)}
                    key={time}
                    className={classnames({
                        [styles.isSelected]: selectedSlot === time,
                    })}
                >
                    {label}
                </button>
            ))}
            <div
                className={classnames(
                    classes?.placeholder,
                    styles.placeholder,
                    {
                        [styles.noAvailabilityTitle]: !showHeader,
                    },
                )}
            >
                {showHeader && (
                    <h4
                        className={
                            isToday ? styles.fullyBookedOrClosed : undefined
                        }
                    >
                        {intl.formatMessage({
                            id: showHeaderIntlId,
                        })}
                    </h4>
                )}
                {isToday && (
                    <>
                        <p className={styles.sameDayAvailability}>
                            {intl.formatMessage(
                                {
                                    id: actionIntlId,
                                },
                                {
                                    link: ((chunk: string) => (
                                        <Link onClick={trackSameDay} to={to}>
                                            {chunk}
                                        </Link>
                                    )) as unknown as React.ReactNode,
                                },
                            )}
                        </p>
                    </>
                )}
            </div>
            <div className={styles.digitalWalkIn}>
                {shouldDisplayWalkInComponent && (
                    <DigitalWalkInBooking
                        setDigitalWalkInModalOpen={setDigitalWalkInModalOpen}
                        eventPrefix={eventPrefix}
                    />
                )}
            </div>
            {hasNearbySlots && (
                <div
                    className={classnames(
                        classes?.nearbyLocations,
                        styles.nearbyLocations,
                    )}
                >
                    <h5 className={styles.nearbyLocationsTitle}>
                        {intl.formatMessage({
                            id: `booking.form.date.visitNearbyClinicText${
                                isMobile ? 'Mobile' : 'Desktop'
                            }`,
                        })}
                    </h5>

                    {location?.nearestLocations.map(
                        ({ location: nearbyLocation }) => (
                            <NearbyLocation
                                key={nearbyLocation._id}
                                location={nearbyLocation}
                                selectedSlot={null}
                                selectSlot={setSelectedNearbySlot}
                                onSeeMore={() =>
                                    changeLocation(nearbyLocation._id)
                                }
                                classes={{
                                    slotButton: styles.nearbySlotButton,
                                    clinicName: classnames(
                                        classes?.clinicName,
                                        styles.darkText,
                                    ),
                                    seeMore: styles.seeMore,
                                    slotsButtons: classnames(
                                        classes?.slotsButtons,
                                        styles.slotsButtons,
                                    ),
                                    root: classes?.nearbyClinicWrapper,
                                }}
                                isMobile={isMobile}
                            />
                        ),
                    )}
                </div>
            )}
        </div>
    );
}
